import { z } from 'zod/v4';

import type { SearchParamPresence } from './stringSearch';

/**
 * A link passes `T` and the route reads `T`; an optional one is `T | undefined`, and its key may be left out.
 * Zod's core types have no `.default()` or `.catch()`, so neither compiles on a schema from `searchParam`.
 */
export type SearchParamSchema<T, TPresence extends SearchParamPresence> = TPresence extends 'required'
    ? z.core.$ZodType<T, T>
    : z.core.$ZodOptional<z.core.$ZodType<T, T>>;

/**
 * `schema` reads both the URL's string and the typed value: the router validates the URL's strings when it matches,
 * then the typed values again when it builds a location (a link, a `setSearch`, the canonical URL on first load). A
 * schema that took only the strings would lose its key on every write of another one.
 */
function searchParamSchema<T, TPresence extends SearchParamPresence>(
    schema: z.ZodType,
    presence: TPresence,
): SearchParamSchema<T, TPresence> {
    // The cast types input and output as `T`: links pass the typed value, and only the URL holds the string.
    // `.catch(undefined)`: the router drops an unreadable optional value from the URL on first load.
    return (presence === 'required' ? schema : schema.optional().catch(undefined)) as unknown as SearchParamSchema<
        T,
        TPresence
    >;
}

/** A finite number spelled the way `String(number)` spells it, so `02` and `1e3` are invalid. */
function isNumberAsWritten(value: string): boolean {
    const number = Number(value);
    return Number.isFinite(number) && String(number) === value;
}

/**
 * Schemas for query params that aren't plain strings, in a route's `validateSearch: z.object({ … })`, next to
 * `z.string()` or `z.string().optional()` for plain ones. A route with only plain strings takes `stringSearch`.
 *
 * An optional value the schema can't read is dropped from the URL on first load; a missing or unreadable
 * required one fails the route, which renders `NotFoundPage`.
 *
 * Each schema reads back exactly what the URLSearchParams codec writes (`String(value)`) and nothing else, so a
 * valid URL keeps its bytes. The types leave out `.default()`, which writes the default into every URL on first
 * load. Don't use it, or `z.coerce`, which re-spells values (`02` becomes `2`), on the plain strings either; handle
 * a missing optional value with `??` where it's read. A value whose `String` doesn't read back (an object, a date)
 * stays a string that its reader parses.
 */
export const searchParam = {
    boolean: <TPresence extends SearchParamPresence>(presence: TPresence) =>
        searchParamSchema<boolean, TPresence>(
            z.union([z.boolean(), z.enum(['true', 'false']).transform((value) => value === 'true')]),
            presence,
        ),

    number: <TPresence extends SearchParamPresence>(presence: TPresence) =>
        searchParamSchema<number, TPresence>(
            z.union([z.number(), z.string().refine(isNumberAsWritten).transform(Number)]),
            presence,
        ),

    /** `a,b` in the URL; an empty value is the empty list. */
    list: <TPresence extends SearchParamPresence>(presence: TPresence) =>
        searchParamSchema<string[], TPresence>(
            z.union([z.array(z.string()), z.string().transform((value) => (value === '' ? [] : value.split(',')))]),
            presence,
        ),

    /** One of `values`: a list of strings or a TypeScript string enum. */
    enum: <
        const TValues extends readonly [string, ...string[]] | Readonly<Record<string, string>>,
        TPresence extends SearchParamPresence,
    >(
        values: TValues,
        presence: TPresence,
    ) =>
        searchParamSchema<TValues extends readonly string[] ? TValues[number] : TValues[keyof TValues], TPresence>(
            z.enum(values as readonly [string, ...string[]]),
            presence,
        ),
};
